/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Row, Table, Tooltip, Col, Typography, Modal, Form, Input, notification, Button, Select, Divider, DatePicker, Breadcrumb, Spin } from 'antd';
const { Title, Text } = Typography;
import { readFile, utils } from "xlsx";
import { Button as CrceButton, Checkbox as CrceCheckbox } from '@crc-evans/ui-components';
import { Plus, FileText, Loader2 } from 'lucide-react';
import { get } from 'lodash';
import moment from 'moment';
import DSRTabList from '../layout/DSRTabs';
import pdf from "../assets/template.pdf"
import { fetchSowDetails } from '../redux/sowDetails/actions';
import {
    fetchDsrLogs,
    updateDsrLogSections,
    toggleDsrShiftSkipped,
    clearTechnicianLog,
    clearMaterialLog,
    clearConsumablesLog,
    clearEquipmentUsageLog,
    clearEhsLog,
    clearDowntimeLog,
    clearProductionLog
} from '../redux/dsr/action';
import { connect } from 'react-redux';
import DSRReportOptions from './DSR/DSRReportOptions';
import DSRReportForm from './DSR/DSRReportForm';
import DSRMainlineSelection from './DSR/DSRMainlineSelection';
import DSRReportDetails from './DSR/DSRReportDetails';
import MaterialConsumptionSection from './DSR/MaterialConsumptionSection';
import ProductionJointsSection from './DSR/ProductionJointsSection';
import EquipmentUsageSection from './DSR/EquipmentUsageSection';
import EHSSection from './DSR/EHSSection';
import TechnicianLogSection from './DSR/TechnicianLogSection';
import DownTimeSection from './DSR/DownTimeSection';
import ConsumablesSection from './DSR/ConsumablesSection';
import DSRSectionBreadcrumb from './DSR/DSRSectionBreadcrumb';
import PqtActualValuesPage from './PQT/PqtActualValuesPage';
import { PROJECT_TABLE_LOADER_INDICATOR, getProjectTableLoadingConfig } from './ProjectTableLoader';
import './DSR.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

const DSR = (props) => {
    const {
        selectedProject,
        dsrLogs,
        isDsrLogsLoading,
        onFetchDsrLogs,
        onFetchSowDetails,
        sowDetails,
        isSowLoading,
        currentDsrLog,
        isSubmitted,
        isUpdatingDsrLogSections,
        isCreatingDsrLog,
        isTechnicianLogLoading,
        isMaterialLogLoading,
        isConsumablesLogLoading,
        isEquipmentUsageLogLoading,
        isEhsLogLoading,
        isDowntimeLogLoading,
        isProductionLogLoading,
        isDsrLogPqtLoading,
        onUpdateDsrLogSections,
        onToggleDsrShiftSkipped,
        onClearTechnicianLog,
        onClearMaterialLog,
        onClearConsumablesLog,
        onClearEquipmentUsageLog,
        onClearEhsLog,
        onClearDowntimeLog,
        onClearProductionLog
    } = props;

    const [isAddMemberVisible, setIsAddMemberVisible] = useState(false);
    const [selectedData, setSelectedDateData] = useState({});
    const [selectedRange, setSelectedRange] = useState([]);
    const [locationData, setLocationData] = useState([]);
    const [selectedPipeline, setSelectedPipeline] = useState('')
    const [filteredData, setFilteredData] = useState([]);
    const [dsrListPage, setDsrListPage] = useState(1);
    const [dsrListPageSize, setDsrListPageSize] = useState(10);
    const [dsrSowId, setDsrSowId] = useState('');
    const [selectPipeline] = Form.useForm();
    const [showReportOptions, setShowReportOptions] = useState(false);
    const [selectedReportType, setSelectedReportType] = useState(null);
    const [selectedMainline, setSelectedMainline] = useState(null);
    const [currentStep, setCurrentStep] = useState('list'); // 'list', 'mainline', 'report-type', 'form', 'section'
    const [activeSection, setActiveSection] = useState(null);
    const [activeShift, setActiveShift] = useState('day');
    const [submittedSections, setSubmittedSections] = useState({});
    const [skipped, setSkipped] = useState({ day: false, night: false, all: false });
    const [selectedDate, setSelectedDate] = useState(null);
    const [isPdfModalVisible, setIsPdfModalVisible] = useState(false);
    const [emailInput, setEmailInput] = useState('');
    const [emailList, setEmailList] = useState([]);
    const [isSharing, setIsSharing] = useState(false);

    // Local state to force re-renders when section skip state changes
    const [sectionSkipState, setSectionSkipState] = useState({});
    const pendingSkipChanges = useRef({});

    // Only spin for the section the user is viewing — other sections' flags can stay true and must not block the UI.
    const isActiveSectionLoading = useMemo(() => {
        if (!activeSection) return false;
        switch (activeSection) {
            case 'technician':
                return isTechnicianLogLoading;
            case 'material':
                return isMaterialLogLoading;
            case 'consumables':
                return isConsumablesLogLoading;
            case 'equipment':
                return isEquipmentUsageLogLoading;
            case 'ehs':
                return isEhsLogLoading;
            case 'downtime':
                return isDowntimeLogLoading;
            case 'production':
                return isProductionLogLoading;
            case 'pqtActualValues':
                return isDsrLogPqtLoading;
            default:
                return false;
        }
    }, [
        activeSection,
        isTechnicianLogLoading,
        isMaterialLogLoading,
        isConsumablesLogLoading,
        isEquipmentUsageLogLoading,
        isEhsLogLoading,
        isDowntimeLogLoading,
        isProductionLogLoading,
        isDsrLogPqtLoading,
    ]);

    useEffect(() => {
        const projId = get(selectedProject, 'id', undefined);
        if (selectedProject && selectedProject.id) {
            setSelectedMainline(sowDetails[0]);
            // Always set date to today when starting a new DSR
            setSelectedDate(moment());
            onFetchDsrLogs({ selectedProject });
            setSelectedRange([]);
            onFetchSowDetails(projId)
        }
    }, [selectedProject]);

    useEffect(() => {
        setLocationData(sowDetails);
    }, [sowDetails]);

    // SOW list loads async after project change; `setSelectedMainline(sowDetails[0])` above often ran with [].
    // Once pipelines exist, pick the pipeline for the current DSR log (or first row) so sections/PQT have a valid sow id.
    useEffect(() => {
        if (currentStep !== 'form' && currentStep !== 'section') return;
        if (!Array.isArray(sowDetails) || sowDetails.length === 0 || selectedMainline) return;
        const sid = currentDsrLog?.sowId ?? currentDsrLog?.sowID;
        const match = sid != null
            ? sowDetails.find((s) => String(s.id) === String(sid) || String(s.sowId) === String(sid))
            : null;
        setSelectedMainline(match || sowDetails[0]);
    }, [sowDetails, selectedMainline, currentStep, currentDsrLog?.sowId, currentDsrLog?.sowID]);

    // Sync skip state from GET dsr-log response (isSkippedDayShift, isSkippedNightShift); updates via PUT toggle-shift-skipped
    useEffect(() => {
        if (currentDsrLog) {
            const day = !!currentDsrLog.isSkippedDayShift;
            const night = !!currentDsrLog.isSkippedNightShift;
            setSkipped({ day, night, all: day && night });
        }
    }, [currentDsrLog?.id, currentDsrLog?.isSkippedDayShift, currentDsrLog?.isSkippedNightShift]);

    // Always set activeShift to 'day' when navigating to form step
    // useEffect(() => {
    //     if (currentStep === 'form') {
    //         setActiveShift('day');
    //     }
    // }, [currentStep]);

    // Update filteredData when dsrLogs changes
    useEffect(() => {
        if (dsrLogs && dsrLogs.length > 0) {
            // If no pipeline is selected, show all logs
            // If a pipeline is selected, filter by that pipeline
            let dataToFilter = [];
            if (!dsrSowId) {
                dataToFilter = dsrLogs;
            } else {
                dataToFilter = dsrLogs.filter(pipeLine => pipeLine.sowId == dsrSowId);
            }
            // Sort by logDate in descending order (latest first)
            const sortedData = [...dataToFilter].sort((a, b) => {
                const dateA = a.logDate ? new Date(a.logDate) : new Date(0);
                const dateB = b.logDate ? new Date(b.logDate) : new Date(0);
                return dateB - dateA; // Descending order (latest first)
            });
            setFilteredData(sortedData);
        } else {
            setFilteredData([]);
        }
    }, [dsrLogs, dsrSowId]);

    useEffect(() => {
        setDsrListPage(1);
    }, [dsrSowId, dsrLogs]);

    // Clear pending skip changes when API call completes
    useEffect(() => {
        if (!isUpdatingDsrLogSections && Object.keys(pendingSkipChanges.current).length > 0) {
            console.log('DSR: API call completed, clearing pending skip changes');
            // Clear pending changes after API call completes
            pendingSkipChanges.current = {};
            setSectionSkipState(prev => ({ ...prev }));
        }
    }, [isUpdatingDsrLogSections]);

    // Update section skip state when currentDsrLog changes
    useEffect(() => {
        if (currentDsrLog?.sectionsSkipped) {
            console.log('DSR: currentDsrLog sectionsSkipped changed, forcing re-render');
            // Force re-render of sections when sectionsSkipped changes
            setSectionSkipState(prev => ({ ...prev }));
        }
    }, [currentDsrLog?.sectionsSkipped]);

    // Clear section data when date or shift changes
    useEffect(() => {
        clearAllSectionData();
    }, [selectedDate, activeShift]);

    // Clear section data when component unmounts
    useEffect(() => {
        return () => {
            clearAllSectionData();
        };
    }, []);

    const handlePipelineChange = (value) => {
        setDsrSowId(value);
        // The filtering will be handled by the useEffect that watches dsrLogs and dsrSowId
    };

    const handleMainlineSelect = (mainline) => {
        setSelectedMainline(mainline);
        // Always set date to today when starting a new DSR
        setSelectedDate(moment());
        setCurrentStep('form');
    };

    const handleDateChange = (date) => {
        setSelectedDate(date);
        // If we have all required data, call the API to get DSR log for the new date
        // This will be handled by the DSRReportDetails component's useEffect
        // when selectedDateValue changes
    };

    const handleReportTypeSelect = (reportType) => {
        setSelectedReportType(reportType);
        setCurrentStep('form');
    };

    const handleReportSubmit = (values) => {
        // Skip state is persisted via toggle-shift-skipped API when user toggles checkboxes
        // Report submission is now handled by DSRReportDetails component
        // This function will be called after successful submission
        console.log('Report submitted:', values);
        setSelectedReportType(null);
        setSelectedMainline(null);
        setSelectedDate(moment()); // Reset to today's date for next DSR
        setCurrentStep('list');
        // Refresh the DSR logs list
        if (selectedProject) {
            onFetchDsrLogs({ selectedProject });
        }
    };

    const handleReportCancel = () => {
        setSelectedReportType(null);
        setSelectedMainline(null);
        setSelectedDate(moment()); // Always reset to today's date
        setCurrentStep('list');
    };

    const clearAllSectionData = () => {
        console.log('DSR: Clearing all section data');
        onClearTechnicianLog();
        onClearMaterialLog();
        onClearConsumablesLog();
        onClearEquipmentUsageLog();
        onClearEhsLog();
        onClearDowntimeLog();
        onClearProductionLog();
    };

    const handleSectionSelect = (sectionKey) => {
        // Prevent selecting shift-based sections if the current shift is skipped (PQT is always allowed)
        const isShiftSkipped = activeShift === 'day' ? skipped.day : skipped.night;
        if (isShiftSkipped && sectionKey !== 'pqtActualValues') {
            return;
        }

        console.log('DSR: Selecting section:', sectionKey);
        // Clear all section data before switching to a new section
        clearAllSectionData();
        setActiveSection(sectionKey);
        setCurrentStep('section');
    };

    const handleSectionCompletion = (sectionKey, isCompleted) => {
        setSubmittedSections(prev => ({
            ...prev,
            [sectionKey]: isCompleted
        }));

        // Get the next section to navigate to
        const nextSection = getNextSection(sectionKey);
        if (nextSection) {
            // Check if the next section should be skipped due to shift being skipped
            const isShiftSkipped = activeShift === 'day' ? skipped.day : skipped.night;
            if (isShiftSkipped) {
                // If shift is skipped, mark all sections as completed and go back to form
                const sectionOrder = ['technician', 'ehs', 'equipment', 'material', 'consumables', 'downtime', 'production'];
                const updatedSubmittedSections = {};
                sectionOrder.forEach(section => {
                    updatedSubmittedSections[section] = true;
                });
                setSubmittedSections(updatedSubmittedSections);
                setCurrentStep('form');
                setActiveSection(null);
                return;
            }

            // Clear all section data before moving to next section
            clearAllSectionData();
            setActiveSection(nextSection);
            setCurrentStep('section');
        } else {
            // If no next section, go back to the form
            setCurrentStep('form');
            setActiveSection(null);
        }
    };

    // Function to get the next section in order
    const getNextSection = (currentSection) => {
        const sectionOrder = ['technician', 'ehs', 'equipment', 'material', 'consumables', 'downtime', 'production'];
        const currentIndex = sectionOrder.indexOf(currentSection);

        if (currentIndex === -1 || currentIndex === sectionOrder.length - 1) {
            return null; // No next section
        }

        return sectionOrder[currentIndex + 1];
    };

    const handleSkipChange = (type, checked) => {
        if (!currentDsrLog?.id) return;
        const shift = type === 'all' ? 'both' : type; // 'day' | 'night' | 'both'
        setSkipped((prev) => {
            if (type === 'all') return { day: checked, night: checked, all: checked };
            const next = { ...prev, [type]: checked };
            next.all = next.day && next.night;
            return next;
        });
        if (type === 'all' && checked) setActiveShift('day');
        onToggleDsrShiftSkipped({ dsrLogId: currentDsrLog.id, shift, skipStatus: checked });
    };

    // Helper function to get section skip state
    const getSectionSkipState = (section) => {
        if (!currentDsrLog || !currentDsrLog.sectionsSkipped) return false;
        const { sectionsSkipped } = currentDsrLog;
        const shiftKeySuffix = activeShift === 'day' ? 'DayShift' : 'NightShift';

        // Create the correct key mapping
        let skipKey;
        switch (section) {
            case 'technician':
                skipKey = `isTechnicianLogSkipped${shiftKeySuffix}`;
                break;
            case 'downtime':
                skipKey = `isDowntimeSkipped${shiftKeySuffix}`;
                break;
            case 'consumables':
                skipKey = `isConsumablesSkipped${shiftKeySuffix}`;
                break;
            case 'equipment':
                skipKey = `isEquipmentUsageSkipped${shiftKeySuffix}`;
                break;
            case 'ehs':
                skipKey = `isEhsSkipped${shiftKeySuffix}`;
                break;
            case 'material':
                skipKey = `isMaterialUsageSkipped${shiftKeySuffix}`;
                break;
            case 'production':
                skipKey = `isProductionJointSkipped${shiftKeySuffix}`;
                break;
            default:
                return false;
        }

        // Check pending changes first (for optimistic updates)
        if (pendingSkipChanges.current.hasOwnProperty(skipKey)) {
            return pendingSkipChanges.current[skipKey] === 1;
        }

        // Fall back to Redux state
        return sectionsSkipped[skipKey] === 1;
    };

    // Helper function to generate submission status fields for a specific section and shift
    const generateSubmissionStatus = (section, shift, isSubmitted = true) => {
        const shiftKeySuffix = shift === 'day' ? 'Dayshift' : 'Nightshift';
        let submittedKey;

        switch (section) {
            case 'technician':
                submittedKey = `isTechnicianLogSubmitted${shiftKeySuffix}`;
                break;
            case 'downtime':
                submittedKey = `isDowntimeSubmitted${shiftKeySuffix}`;
                break;
            case 'consumables':
                submittedKey = `isConsumablesSubmitted${shiftKeySuffix}`;
                break;
            case 'equipment':
                submittedKey = `isEquipmentUsageSubmitted${shiftKeySuffix}`;
                break;
            case 'ehs':
                submittedKey = `isEhsIssuesSubmitted${shiftKeySuffix}`;
                break;
            case 'material':
                submittedKey = `isMaterialUsageSubmitted${shiftKeySuffix}`;
                break;
            case 'production':
                submittedKey = `isProductionJointSubmitted${shiftKeySuffix}`;
                break;
            default:
                return {};
        }

        return { [submittedKey]: isSubmitted };
    };

    // Centralized skip handler for all sections
    const handleSectionSkipChange = (section, checked) => {
        if (!currentDsrLog?.id) return;

        const shiftKeySuffix = activeShift === 'day' ? 'DayShift' : 'NightShift';

        // Create the correct key mapping for local state
        let skipKey;
        switch (section) {
            case 'technician':
                skipKey = `isTechnicianLogSkipped${shiftKeySuffix}`;
                break;
            case 'downtime':
                skipKey = `isDowntimeSkipped${shiftKeySuffix}`;
                break;
            case 'consumables':
                skipKey = `isConsumablesSkipped${shiftKeySuffix}`;
                break;
            case 'equipment':
                skipKey = `isEquipmentUsageSkipped${shiftKeySuffix}`;
                break;
            case 'ehs':
                skipKey = `isEhsSkipped${shiftKeySuffix}`;
                break;
            case 'material':
                skipKey = `isMaterialUsageSkipped${shiftKeySuffix}`;
                break;
            case 'production':
                skipKey = `isProductionJointSkipped${shiftKeySuffix}`;
                break;
            default:
                return;
        }

        // Store pending change for optimistic updates
        pendingSkipChanges.current[skipKey] = checked ? 1 : 0;

        // Force re-render to show optimistic update immediately
        setSectionSkipState(prev => ({ ...prev }));

        // Optimistically update the currentDsrLog state for immediate UI feedback
        if (currentDsrLog) {
            const currentSectionsSkipped = currentDsrLog.sectionsSkipped || {};
            currentDsrLog.sectionsSkipped = {
                ...currentSectionsSkipped,
                [skipKey]: checked ? 1 : 0
            };
        }

        // Generate submission status - if section is being skipped, mark as submitted
        const submissionStatus = generateSubmissionStatus(section, activeShift, checked);

        // Call API to persist the changes
        const currentSectionsSkipped = currentDsrLog.sectionsSkipped || {};
        const updatedSectionsSkipped = {
            ...currentSectionsSkipped,
            [skipKey]: checked ? 1 : 0
        };

        props.onUpdateDsrLogSections({
            dsrLogId: currentDsrLog.id,
            sectionsSkipped: updatedSectionsSkipped,
            submissionStatus: submissionStatus
        });
    };

    // Get section title for breadcrumb
    const getSectionTitle = (section) => {
        switch (section) {
            case 'technician':
                return 'Technician Log';
            case 'downtime':
                return 'DownTime Data';
            case 'consumables':
                return 'Consumables';
            case 'equipment':
                return 'Equipment Usage';
            case 'ehs':
                return 'EHS Issue';
            case 'material':
                return 'Material Consumption';
            case 'production':
                return 'Production Joints';

            default:
                return section;
        }
    };

    const renderBreadcrumbs = () => {
        return (
            <div
                className="modern-dsr-form-toolbar-breadcrumbs modern-dsr-form-toolbar-breadcrumbs--unified"
                style={{ width: '100%' }}
            >
                <div className="modern-dsr-form-toolbar-bc-left">
                    <Breadcrumb className="modern-dsr-form-toolbar-breadcrumb-trace" style={{ margin: 0 }}>
                        <Breadcrumb.Item>
                            <a
                                href="#dsr-list"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setCurrentStep('list');
                                }}
                                className={`modern-dsr-breadcrumb-link${currentStep === 'list' ? ' modern-dsr-breadcrumb-link--active' : ''}`}
                            >
                                DSR List
                            </a>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>
                            <a
                                href="#dsr-add-report"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setCurrentStep('form');
                                }}
                                className="modern-dsr-breadcrumb-link"
                            >
                                Add Report
                            </a>
                        </Breadcrumb.Item>
                        {currentStep === 'section' && activeSection && (
                            <Breadcrumb.Item>
                                <span className="modern-dsr-breadcrumb-current">
                                    {activeSection === 'pqtActualValues'
                                        ? 'PQT'
                                        : `${activeShift.charAt(0).toUpperCase() + activeShift.slice(1)} Shift - ${getSectionTitle(activeSection)}`}
                                </span>
                            </Breadcrumb.Item>
                        )}
                    </Breadcrumb>
                </div>
                {currentStep === 'section' && activeSection && activeSection !== 'pqtActualValues' && (
                    <div className="modern-dsr-form-toolbar-bc-right">
                        <DSRSectionBreadcrumb
                            selectedDate={selectedDate}
                            onDateChange={handleDateChange}
                            shift={activeShift}
                            sectionTitle={getSectionTitle(activeSection)}
                            skipped={getSectionSkipState(activeSection)}
                            onSkipChange={(e) => handleSectionSkipChange(activeSection, e.target.checked)}
                            isSubmitted={isSubmitted}
                            isUpdatingDsrLogSections={isUpdatingDsrLogSections}
                            isShiftSkipped={activeShift === 'day' ? skipped.day : skipped.night}
                        />
                    </div>
                )}
            </div>
        );
    };

    const dsrPaginatedData = useMemo(() => {
        const start = (dsrListPage - 1) * dsrListPageSize;
        return filteredData.slice(start, start + dsrListPageSize);
    }, [filteredData, dsrListPage, dsrListPageSize]);

    const renderContent = () => {
        switch (currentStep) {
            case 'mainline':
                return (
                    <div className="modern-dsr-page-spin-outer">
                        <Spin
                            spinning={isSowLoading}
                            tip="Loading mainlines..."
                            size="large"
                            indicator={PROJECT_TABLE_LOADER_INDICATOR}
                            className="modern-dsr-page-spin"
                        >
                            <DSRMainlineSelection
                                mainlines={locationData}
                                onSelectMainline={handleMainlineSelect}
                                selectedMainline={selectedMainline}
                            />
                        </Spin>
                    </div>
                );
            case 'form':
                return (
                    <div className="modern-dsr-page-spin-outer">
                        <Spin
                            spinning={isCreatingDsrLog}
                            tip="Loading DSR data..."
                            size="large"
                            indicator={PROJECT_TABLE_LOADER_INDICATOR}
                            className="modern-dsr-page-spin"
                        >
                            <DSRReportDetails
                                onSubmit={handleReportSubmit}
                                onSkip={handleReportCancel}
                                onSectionSelect={handleSectionSelect}
                                activeShift={activeShift}
                                setActiveShift={setActiveShift}
                                submittedSections={submittedSections}
                                skipped={skipped}
                                setSkipped={setSkipped}
                                selectedProject={selectedProject}
                                selectedMainline={selectedMainline}
                                selectedDate={selectedDate}
                                isSubmitted={isSubmitted}
                                onDateChange={handleDateChange}
                            />
                        </Spin>
                    </div>
                );
            case 'section':
                return (
                    <div className="modern-dsr-page-spin-outer">
                        <Spin
                            spinning={isActiveSectionLoading}
                            tip="Loading section data..."
                            size="large"
                            indicator={PROJECT_TABLE_LOADER_INDICATOR}
                            className="modern-dsr-page-spin"
                        >
                        {activeSection === 'material' && (
                            <MaterialConsumptionSection
                                shift={activeShift}
                                selectedDate={selectedDate}
                                selectedProject={selectedProject}
                                selectedMainline={selectedMainline}
                                currentDsrLog={currentDsrLog}
                                isSkipped={getSectionSkipState('material')}
                                isSubmitted={isSubmitted}
                                onComplete={(isCompleted, sectionKey) => handleSectionCompletion(sectionKey || 'material', isCompleted)}
                                onCancel={handleSectionCancel}
                                isShiftSkipped={activeShift === 'day' ? skipped.day : skipped.night}
                                onSkipChange={(e) => handleSectionSkipChange('material', e.target.checked)}
                            />
                        )}
                        {activeSection === 'production' && (
                            <ProductionJointsSection
                                shift={activeShift}
                                selectedDate={selectedDate}
                                selectedProject={selectedProject}
                                selectedMainline={selectedMainline}
                                sowDetails={locationData}
                                currentDsrLog={currentDsrLog}
                                isSkipped={getSectionSkipState('production')}
                                isSubmitted={isSubmitted}
                                onComplete={(isCompleted, sectionKey) => handleSectionCompletion(sectionKey || 'production', isCompleted)}
                                onCancel={handleSectionCancel}
                                isShiftSkipped={activeShift === 'day' ? skipped.day : skipped.night}
                                onSkipChange={(e) => handleSectionSkipChange('production', e.target.checked)}
                            />
                        )}
                        {activeSection === 'equipment' && (
                            <EquipmentUsageSection
                                shift={activeShift}
                                selectedDate={selectedDate}
                                selectedProject={selectedProject}
                                selectedMainline={selectedMainline}
                                currentDsrLog={currentDsrLog}
                                isSkipped={getSectionSkipState('equipment')}
                                isSubmitted={isSubmitted}
                                onComplete={(isCompleted, sectionKey) => handleSectionCompletion(sectionKey || 'equipment', isCompleted)}
                                onCancel={handleSectionCancel}
                                isShiftSkipped={activeShift === 'day' ? skipped.day : skipped.night}
                                onSkipChange={(e) => handleSectionSkipChange('equipment', e.target.checked)}
                            />
                        )}
                        {activeSection === 'ehs' && (
                            <EHSSection
                                shift={activeShift}
                                selectedDate={selectedDate}
                                selectedProject={selectedProject}
                                selectedMainline={selectedMainline}
                                currentDsrLog={currentDsrLog}
                                isSkipped={getSectionSkipState('ehs')}
                                isSubmitted={isSubmitted}
                                onComplete={(isCompleted, sectionKey) => handleSectionCompletion(sectionKey || 'ehs', isCompleted)}
                                onCancel={handleSectionCancel}
                                isShiftSkipped={activeShift === 'day' ? skipped.day : skipped.night}
                                onSkipChange={(e) => handleSectionSkipChange('ehs', e.target.checked)}
                            />
                        )}
                        {activeSection === 'technician' && (
                            <TechnicianLogSection
                                key={`technician-${selectedDate}-${activeShift}`}
                                shift={activeShift}
                                selectedDate={selectedDate}
                                selectedProject={selectedProject}
                                selectedMainline={selectedMainline}
                                currentDsrLog={currentDsrLog}
                                isSkipped={getSectionSkipState('technician')}
                                isSubmitted={isSubmitted}
                                onComplete={(isCompleted, sectionKey) => handleSectionCompletion(sectionKey || 'technician', isCompleted)}
                                onCancel={handleSectionCancel}
                                isShiftSkipped={activeShift === 'day' ? skipped.day : skipped.night}
                                onSkipChange={(e) => handleSectionSkipChange('technician', e.target.checked)}
                            />
                        )}
                        {activeSection === 'downtime' && (
                            <DownTimeSection
                                shift={activeShift}
                                selectedDate={selectedDate}
                                selectedProject={selectedProject}
                                selectedMainline={selectedMainline}
                                currentDsrLog={currentDsrLog}
                                isSkipped={getSectionSkipState('downtime')}
                                isSubmitted={isSubmitted}
                                onComplete={(isCompleted, sectionKey) => handleSectionCompletion(sectionKey || 'downtime', isCompleted)}
                                onCancel={handleSectionCancel}
                                isShiftSkipped={activeShift === 'day' ? skipped.day : skipped.night}
                                onSkipChange={(e) => handleSectionSkipChange('downtime', e.target.checked)}
                            />
                        )}
                        {activeSection === 'consumables' && (
                            <ConsumablesSection
                                shift={activeShift}
                                selectedDate={selectedDate}
                                selectedProject={selectedProject}
                                selectedMainline={selectedMainline}
                                currentDsrLog={currentDsrLog}
                                isSkipped={getSectionSkipState('consumables')}
                                isSubmitted={isSubmitted}
                                onComplete={(isCompleted, sectionKey) => handleSectionCompletion(sectionKey || 'consumables', isCompleted)}
                                onCancel={handleSectionCancel}
                                isShiftSkipped={activeShift === 'day' ? skipped.day : skipped.night}
                                onSkipChange={(e) => handleSectionSkipChange('consumables', e.target.checked)}
                            />
                        )}
                        {activeSection === 'pqtActualValues' && (
                            <div className="modern-dsr-pqt-section-host" style={{ padding: '0 8px 8px' }}>
                                <PqtActualValuesPage
                                    selectedProject={selectedProject}
                                    sowDetails={locationData}
                                    selectedDate={selectedDate}
                                    selectedMainline={selectedMainline}
                                    onCancel={handleSectionCancel}
                                />
                            </div>
                        )}
                        {!['material', 'production', 'equipment', 'ehs', 'technician', 'downtime', 'consumables', 'pqtActualValues'].includes(activeSection) && (
                            <div style={{ padding: 32, textAlign: 'center' }}>
                                <h2>Section: {activeSection}</h2>
                                <CrceButton
                                    label="Back to Report"
                                    variant="primary"
                                    size="medium"
                                    onClick={() => setCurrentStep('form')}
                                />
                            </div>
                        )}
                    </Spin>
                    </div>
                );
            default:
                return (
                    <div className="modern-dsr-page-spin-outer modern-dsr-page-spin-outer--list">
                        <div className="modern-dsr-table-wrap">
                            <Table
                                pagination={{
                                    current: dsrListPage,
                                    pageSize: dsrListPageSize,
                                    total: filteredData.length,
                                    showSizeChanger: true,
                                    pageSizeOptions: ['10', '20', '50', '100'],
                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                                    onChange: (page, pageSize) => {
                                        setDsrListPage(page);
                                        setDsrListPageSize(pageSize);
                                    },
                                    onShowSizeChange: (_current, size) => {
                                        setDsrListPageSize(size);
                                        setDsrListPage(1);
                                    },
                                    className: 'heat-profile-pagination',
                                }}
                                columns={columns}
                                dataSource={dsrPaginatedData}
                                className="modern-dsr-table pipeline-table"
                                loading={getProjectTableLoadingConfig(isDsrLogsLoading)}
                                rowKey="id"
                            />
                        </div>
                    </div>
                );
        }
    };

    const generatePDF = () => {
        let link = document.createElement('a')
        link.href = pdf
        link.download = "test.pdf"
        link.click()
    }

    const columns = [
        {
            title: 'Date',
            dataIndex: 'logDate',
            key: 'date',
            render: (text, record) => {
                return (
                    <div className="modern-dsr-row-click" onClick={() => { showModal(record) }}>
                        {text ? moment(text).format('DD MMM YYYY') : '-'}
                    </div>
                );
            },
            align: 'center',
        },
        {
            title: 'Created By',
            key: 'createdBy',
            render: (text, record) => {
                return (
                    <div className="modern-dsr-row-click" onClick={() => { showModal(record) }}>
                        {record.userFirstName} {record.userLastName}
                    </div>
                );
            },
            align: 'center'
        },
        // {
        //     title: 'Created Time',
        //     dataIndex: 'logSubmitDate',
        //     key: 'createdTime',
        //     align: 'center',
        //     render: (text) => {
        //         return (
        //             <div>{text ? moment(text).format('DD MMM YYYY'): '-'}</div>
        //         );
        //     },
        // },
        // {
        //     title: 'Pipeline',
        //     dataIndex: 'pipelineSection',
        //     key: 'pipelineSection',
        //     align: 'center',
        //     render: (text, record) => {
        //         return (
        //             <div style={{ cursor: 'pointer' }} onClick={() => { showModal(record) }}>
        //                 {text}
        //             </div>
        //         );
        //     },
        // },
        // {
        //     title: 'Comments',
        //     dataIndex: 'comments',
        //     key: 'comments',
        //     align: 'center',
        //     render: (text, record) => {
        //         return (
        //             <div style={{ cursor: 'pointer' }} onClick={() => { showModal(record) }}>
        //                 { text }
        //             </div>
        //         );
        //     },
        // },
        {
            title: 'Report',
            dataIndex: 'reportDetails',
            key: 'reportDetails',
            align: 'center',
            render: (text, record) => {
                return (
                    <div className="modern-dsr-report-cell">
                        {record && record.reportDetails !== null && JSON.stringify(record.reportDetails) != '{}' ?
                            <Tooltip title="View / Download Report" >
                                <a
                                    href={record?.reportDetails?.fileUrl}
                                    className="modern-dsr-report-link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <FileText size={18} strokeWidth={2} aria-hidden />
                                </a>
                            </Tooltip>
                            :
                            <Tooltip title="Report Not Generated" >
                                <span className="modern-dsr-report-link modern-dsr-report-link--disabled" aria-hidden>
                                    <FileText size={18} strokeWidth={2} />
                                </span>
                            </Tooltip>
                        }
                    </div>
                );
            },
        }
    ];

    const showModal = (record) => {
        setDsrSowId(record.sowId)
        setIsAddMemberVisible(true);
        setSelectedDateData(record);
    }

    const handleCancel = () => {
        setIsAddMemberVisible(false);
    }

    const handlePdfClick = () => {
        setIsPdfModalVisible(true);
    }

    const handlePdfModalCancel = () => {
        setIsPdfModalVisible(false);
    }

    const handleEmailInputChange = (e) => setEmailInput(e.target.value);

    const handleAddEmail = () => {
        const emails = emailInput.split(',').map(e => e.trim()).filter(e => e);
        setEmailList(prev => [...new Set([...prev, ...emails])]);
        setEmailInput('');
    };

    const handleRemoveEmail = (email) => {
        setEmailList(prev => prev.filter(e => e !== email));
    };

    const handlePdfModalOk = async () => {
        setIsSharing(true);
        try {
            const dsrLogId = currentDsrLog?.id;
            const projectId = selectedProject?.id;
            const emailToDestinations = encodeURIComponent(emailList.join(','));
            const url = `${process.env.REACT_APP_BASE_URL}/dsr-log/invoke-generate-report?dsrLogId=${dsrLogId}&projectId=${projectId}&generateFlashReport=1&generatePdfReport=1&emailToDestinations=${emailToDestinations}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: { 'Accept': 'application/json' }
            });
            if (!response.ok) throw new Error('Failed to generate report');
            notification.success({ message: 'Report shared successfully!' });
            setIsPdfModalVisible(false);
            setEmailList([]);
        } catch (err) {
            notification.error({ message: 'Failed to share report', description: err.message });
        } finally {
            setIsSharing(false);
        }
    };

    const handleCustomDateSelect = async (date, dateString) => {
        if (date) {
            setSelectedRange(date);
            let dateObj = {
                fromDate: moment(dateString[0]).format('YYYY-MM-DD HH:mm:ss'),
                toDate: moment(dateString[1]).format('YYYY-MM-DD HH:mm:ss'),
            }
            if (selectedProject && selectedProject.id) {
                onFetchDsrLogs({ selectedProject, dateObj });
            } else {
                notification.warning({
                    message: 'Please select Project to create Pipeline'
                });
            }
        } else {
            setSelectedRange(null);
            onFetchDsrLogs({ selectedProject });
        }
    }

    const handleSectionCancel = () => {
        // When canceling from a section, go back to the "Add Report" page (form step)
        setCurrentStep('form');
        setActiveSection(null);
    };

    return (
        <div className="h-100 modern-dsr-tab" style={{ overflow: 'hidden' }}>
            {currentStep !== 'list' && (
                <div className="modern-dsr-form-toolbar">
                    <div className="modern-dsr-form-toolbar-breadcrumbs">
                        {renderBreadcrumbs()}
                    </div>
                    {currentStep === 'form' && (
                        <div className="modern-dsr-form-toolbar-right">
                            <div className="modern-dsr-form-date-wrap">
                                <DatePicker
                                    className="modern-dsr-form-date-picker"
                                    size="middle"
                                    format="DD-MMM-YYYY"
                                    placeholder="Select DSR date"
                                    value={selectedDate ? moment(selectedDate) : null}
                                    onChange={(date) => handleDateChange(date ? moment(date).startOf('day') : null)}
                                    disabledDate={(current) => current && current > moment().endOf('day')}
                                    allowClear={false}
                                    dropdownClassName="modern-dsr-date-dropdown"
                                />
                            </div>

                            <div className="modern-dsr-form-skip-checks">
                                <CrceCheckbox
                                    label="Skip Day Shift"
                                    checked={skipped.day}
                                    onChange={(v) => {
                                        const checked = typeof v === 'boolean' ? v : (v?.target?.checked ?? false);
                                        handleSkipChange('day', checked);
                                    }}
                                    disabled={isSubmitted || (skipped.all && !skipped.day)}
                                    borderColor="#00a88e"
                                />
                                <CrceCheckbox
                                    label="Skip Night Shift"
                                    checked={skipped.night}
                                    onChange={(v) => {
                                        const checked = typeof v === 'boolean' ? v : (v?.target?.checked ?? false);
                                        handleSkipChange('night', checked);
                                    }}
                                    disabled={isSubmitted || (skipped.all && !skipped.night)}
                                    borderColor="#00a88e"
                                />
                                <CrceCheckbox
                                    label="Skip DSR Report"
                                    checked={skipped.all}
                                    onChange={(v) => {
                                        const checked = typeof v === 'boolean' ? v : (v?.target?.checked ?? false);
                                        handleSkipChange('all', checked);
                                    }}
                                    disabled={isSubmitted}
                                    borderColor="#00a88e"
                                />
                            </div>

                            <div className="modern-dsr-form-toolbar-pqt">
                                <CrceButton
                                    label="Add PQT"
                                    variant="primary"
                                    size="small"
                                    icon={Plus}
                                    onClick={() => handleSectionSelect('pqtActualValues')}
                                />
                            </div>
                        </div>
                    )}

                </div>
            )}
            {currentStep === 'list' && (
                <div className="inspection-logs p-0 d-flex mb-3 justify-content-between align-items-center tab-content-title">
                    <div className="modern-dsr-list-toolbar">
                        <div className="modern-dsr-toolbar-actions">
                            <div className="modern-dsr-range-picker-wrap">
                                <DatePicker.RangePicker
                                    className="modern-dsr-range-picker"
                                    allowClear={true}
                                    format="DD-MMM-YYYY"
                                    onChange={handleCustomDateSelect}
                                    value={selectedRange}
                                    placeholder={['Start date', 'End date']}
                                    dropdownClassName="modern-dropdown-popup heat-profile-datepicker-dropdown"
                                    getPopupContainer={() => document.body}
                                    separator={<span className="modern-dsr-range-sep">–</span>}
                                />
                            </div>
                            <CrceButton
                                label="Add DSR"
                                variant="primary"
                                size="small"
                                icon={Plus}
                                onClick={() => setCurrentStep('form')}
                            />
                        </div>
                    </div>
                </div>
            )}
            {/* <div className="custom-line" style={{}} /> */}

            <div className="modern-dsr-table-scroll-host" style={{
                minHeight: 'min(520px, calc(100vh - 300px))',
                overflowX: 'hidden',
                padding: '0 12px 0 16px',
            }}>
                {renderContent()}
            </div>

            <Modal
                width="98vw"
                style={{ top: 12, paddingBottom: 12 }}
                wrapClassName="modern-dsr-modal-wrap modern-dsr-modal-wrap--detail"
                centered
                title={
                    <div className="modern-dsr-modal-detail-title">
                        <span className="modern-dsr-modal-detail-title-text">Daily Site Report</span>
                        <span className="modern-dsr-modal-detail-title-date">
                            {selectedData?.logDate ? moment(selectedData.logDate).format('DD MMM YYYY') : '—'}
                        </span>
                    </div>
                }
                visible={isAddMemberVisible}
                footer={
                    <div className="modern-dsr-modal-detail-footer-actions">
                        <CrceButton label="Cancel" variant="tertiary" size="medium" onClick={handleCancel} />
                    </div>
                }
            >
                <div>
                    <DSRTabList selectedProject={selectedProject} selectedData={selectedData} dsrSowId={dsrSowId} />
                </div>
            </Modal>

            {/* PDF Generation Modal */}
            <Modal
                title="Generate Report"
                visible={isPdfModalVisible}
                onCancel={handlePdfModalCancel}
                centered
                wrapClassName="modern-dsr-modal-wrap"
                footer={[
                    <CrceButton
                        key="cancel"
                        label="Cancel"
                        variant="tertiary"
                        size="medium"
                        onClick={handlePdfModalCancel}
                        disabled={isSharing}
                    />,
                    <CrceButton
                        key="share"
                        label="Share"
                        variant="primary"
                        size="medium"
                        onClick={handlePdfModalOk}
                        disabled={emailList.length === 0 || isSharing}
                    />,
                ]}
            >
                <div>
                    <div style={{ marginBottom: 16 }}>
                        <label>Email</label>
                        <Input
                            placeholder="Enter emails followed by comma (,)"
                            value={emailInput}
                            onChange={handleEmailInputChange}
                            onPressEnter={handleAddEmail}
                            disabled={isSharing}
                        />
                        <div style={{ marginTop: 8 }}>
                            <CrceButton
                                label="Add"
                                variant="primary"
                                size="small"
                                onClick={handleAddEmail}
                                disabled={!emailInput.trim() || isSharing}
                            />
                        </div>
                        <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
                            eg: 1234@mail.com, 234@email.com
                        </div>
                        <div style={{ marginTop: 8 }}>
                            {emailList.map(email => (
                                <span key={email} style={{ marginRight: 8 }}>
                                    {email}
                                    <Button size="small" type="link" onClick={() => handleRemoveEmail(email)}>x</Button>
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

const mapStateToProps = ({ dsrReducer, sow, projectPqtReportsReducer }) => {
    const dsrLogs = get(dsrReducer, 'dsrLogsList', []);
    const isDsrLogsLoading = get(dsrReducer, 'isDsrLogsLoading', false);
    const currentDsrLog = get(dsrReducer, 'currentDsrLog', null);
    const isSubmitted = currentDsrLog && currentDsrLog.logSubmitDate ? true : false;
    const sowDetails = get(sow, 'sowData', []);
    const isSowLoading = get(sow, 'isSowLoading', false);
    const isUpdatingDsrLogSections = get(dsrReducer, 'isUpdatingDsrLogSections', false);
    const isCreatingDsrLog = get(dsrReducer, 'isCreatingDsrLog', false);

    // Get all section loading states
    const isTechnicianLogLoading = get(dsrReducer, 'isTechnicianLogLoading', false);
    const isMaterialLogLoading = get(dsrReducer, 'isMaterialLogLoading', false);
    const isConsumablesLogLoading = get(dsrReducer, 'isConsumablesLogLoading', false);
    const isEquipmentUsageLogLoading = get(dsrReducer, 'isEquipmentUsageLogLoading', false);
    const isEhsLogLoading = get(dsrReducer, 'isEhsLogLoading', false);
    const isDowntimeLogLoading = get(dsrReducer, 'isDowntimeLogLoading', false);
    const isProductionLogLoading = get(dsrReducer, 'isProductionLogLoading', false);

    const isDsrLogPqtLoading = get(projectPqtReportsReducer, 'isDsrLogPqtLoading', false);

    return {
        dsrLogs,
        isDsrLogsLoading,
        currentDsrLog,
        isSubmitted,
        sowDetails,
        isSowLoading,
        isUpdatingDsrLogSections,
        isCreatingDsrLog,
        isTechnicianLogLoading,
        isMaterialLogLoading,
        isConsumablesLogLoading,
        isEquipmentUsageLogLoading,
        isEhsLogLoading,
        isDowntimeLogLoading,
        isProductionLogLoading,
        isDsrLogPqtLoading,
    };
}

const mapDispatchToProps = {
    onFetchDsrLogs: fetchDsrLogs,
    onFetchSowDetails: fetchSowDetails,
    onUpdateDsrLogSections: updateDsrLogSections,
    onToggleDsrShiftSkipped: toggleDsrShiftSkipped,
    onClearTechnicianLog: clearTechnicianLog,
    onClearMaterialLog: clearMaterialLog,
    onClearConsumablesLog: clearConsumablesLog,
    onClearEquipmentUsageLog: clearEquipmentUsageLog,
    onClearEhsLog: clearEhsLog,
    onClearDowntimeLog: clearDowntimeLog,
    onClearProductionLog: clearProductionLog
}

export default connect(mapStateToProps, mapDispatchToProps)(DSR);
